// Build-time flag for the file-hosting link sources (Yandex Disk, Google
// Drive, Zoom) on the landings. OFF (default, unset) renders exactly the
// pre-launch copy and icon rows; ON lists the new sources.
//
// Copy in the source files is written in the ON form. When the flag is OFF,
// data modules pass their exported copy through `withoutFileHostingSources`,
// which rewrites every string with the locale rules (see
// file-hosting-sources-ru.ts / -intl.ts). Proof that OFF equals the
// pre-launch text: scripts/verify-file-hosting-sources-off.mjs.
//
// This module ships in the shared Scriptario chunk, so it holds no copy.

export const featureFileHostingSources =
  process.env.NEXT_PUBLIC_FEATURE_FILE_HOSTING_SOURCES?.trim() === "true";

export type CopyRule = readonly [pattern: string | RegExp, replacement: string];

export function applyCopyRules(text: string, rules: readonly CopyRule[]): string {
  let out = text;
  for (const [pattern, replacement] of rules) {
    out = typeof pattern === "string" ? out.split(pattern).join(replacement) : out.replace(pattern, replacement);
  }
  return out;
}

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** Deep-maps strings in plain data (arrays / plain objects); React elements,
 *  functions and class instances are returned as is. */
export function mapCopyStrings<T>(value: T, map: (text: string) => string): T {
  if (typeof value === "string") return map(value) as T;
  if (Array.isArray(value)) return value.map((item) => mapCopyStrings(item, map)) as T;
  if (value && typeof value === "object" && isPlainObject(value) && !("$$typeof" in value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, mapCopyStrings(item, map)]),
    ) as T;
  }
  return value;
}

/** Identity when the flag is ON; otherwise strips the new sources. */
export function withoutFileHostingSources<T>(value: T, rules: readonly CopyRule[], enabled = featureFileHostingSources): T {
  if (enabled) return value;
  return mapCopyStrings(value, (text) => applyCopyRules(text, rules));
}
